import React from 'react';
import { css } from '@emotion/css';
import { Indicator } from 'is24-corecss';
import { Trans } from 'react-i18next';
import holdMoney from './hold-money.svg';
import { campaigns } from '@/components/common/campaigns';
import { useIntl } from '@/i18n';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';

export type DepositGuaranteePromoTab = 'mieterplus' | 'wohnenplus';

const styles = {
  banner: css`
    display: flex;
    align-items: center;
    gap: var(--spacing-800);
    margin-top: var(--spacing-1000);
    padding: var(--spacing-600) var(--spacing-800);
    border-radius: var(--border-radius-100);
    background: var(--color-background-weak);
    color: var(--color-text-default);
    text-decoration: none;

    &:hover,
    &:active {
      color: var(--color-text-default);
      text-decoration: none;
    }

    &:focus-visible {
      outline: 2px solid var(--color-border-strong);
      outline-offset: 4px;
    }

    @media (max-width: 668px) {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-400);
      padding: var(--spacing-400);
    }
  `,
  illustration: css`
    flex: 0 0 auto;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--spacing-200);
    min-width: 0;
    flex: 1;
    overflow-wrap: anywhere;
  `,
  cta: css`
    display: inline-flex;
    align-items: center;
    min-height: 40px;
    padding: 0 var(--spacing-400);
    font-size: var(--size-body-medium);
    font-weight: 700;
    line-height: var(--line-height-body-medium);
    border-radius: var(--border-radius-rounded);
    background: var(--color-background-button-default-default);
    color: var(--color-text-button-inversive);
  `,
};

export const DepositGuaranteeBanner = ({ tab }: { tab: DepositGuaranteePromoTab }) => {
  const { t } = useIntl();
  const handleClick = () => {
    // A native link opens immediately, even if reporting is unavailable.
    void report(
      {
        ...TrackingEvents.MEINPLUS_DEPOSIT_GUARANTEE.PROMO_CLICK,
        parameter1: tab === 'mieterplus' ? 'rent' : 'living',
      },
      true,
      tab,
    ).catch(() => {});
  };

  return (
    <a
      className={`${styles.banner} margin-bottom-xxl`}
      href={campaigns.MEINPLUS_DEPOSIT_GUARANTEE[tab]}
      target='_blank'
      rel='noopener noreferrer'
      onClick={handleClick}
      data-testid='deposit-guarantee-banner'
    >
      <img className={styles.illustration} src={holdMoney} alt='' aria-hidden='true' width={96} height={96} />
      <div className={styles.content}>
        <span className='font-heading-xsmall-bold'>
          <Trans
            i18nKey='depositGuaranteeBanner.title'
            components={{ Indicator: <Indicator asSpan appearance='plus' /> }}
          />
        </span>
        <span className='font-body-large-regular'>{t({ id: 'depositGuaranteeBanner.description' })}</span>
        <div className='margin-top-s'>
          <span className={styles.cta}>{t({ id: 'depositGuaranteeBanner.cta' })}</span>
        </div>
      </div>
    </a>
  );
};
